import {
  FC,
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box, Button, Popover } from "@mui/material";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import { FilterTree } from "@/components/pages/common/filters-tree/filters-selection/FilterTree";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  FilterTreeNode,
  useGetUsersMeOrganisationsCurrentPossibleFiltersQuery,
} from "@/services/cloudchipr.api";
import { useMenuHook } from "@/utils/hooks/useMenu.hook";
import { calculateFilterTreeFiltersCount } from "@/components/pages/common/filters-tree/utils/helpers/calculateFilterTreeFiltersCount";
import { removeFiltersWithEmptyValues } from "@/components/pages/common/filters-tree/utils/helpers/removeFiltersWithEmptyValues";
import { isDeepEqual } from "@/utils/is-deep-equal";
import { filterTreeFiltersAreInvalid } from "@/components/pages/common/filters-tree/utils/helpers/filterTreeFiltersAreInvalid";
import { FilterTreeSimpleActions } from "@/components/pages/common/filters-tree/filters-selection/components/FilterTreeSimpleActions";
import { updateSavingsOpportunityCurrentViewFiltersThunk } from "@/store/savings-opportunities/thunks/views-hierarchy/actions/config/updateSavingsOpportunityCurrentViewFiltersThunk";
import { savingsOpportunitiesCurrentViewFiltersSelector } from "@/store/savings-opportunities/selectors/views-hierarchy/current-view/savingsOpportunitiesCurrentViewFiltersSelector";
import { addIdsToFilterTreeItems } from "@/components/pages/common/filters-tree/utils/helpers/ids-manipulations/addIdsToFilterTreeItems";
import { FilterTreeNodeWithId } from "@/components/pages/common/filters-tree/utils/types/common";
import { savingsOpportunityCurrentViewProtectedSelector } from "@/store/savings-opportunities/selectors/views-hierarchy/current-view/savingsOpportunityCurrentViewProtectedSelector";
import { savingsOpportunitiesCurrentViewIdSelector } from "@/store/savings-opportunities/selectors/views-hierarchy/current-view/savingsOpportunitiesCurrentViewIdSelector";

export const SavingsOpportunitiesFilters: FC = () => {
  const dispatch = useAppDispatch();

  const { data: possibleFilters, isLoading } =
    useGetUsersMeOrganisationsCurrentPossibleFiltersQuery({
      source: "opportunity",
    });

  const filterTreeRefForDefaultView = useRef<FilterTreeNodeWithId>({
    node_type: "group",
    operator: "and",
    items: [],
  });

  const { open, closeMenu, anchor, openMenu } = useMenuHook();
  const [filterTreeLocal, setFilterTreeLocal] = useState<
    FilterTreeNodeWithId | undefined
  >(undefined);

  const filtersCount = useMemo(
    () => calculateFilterTreeFiltersCount(filterTreeLocal),
    [filterTreeLocal],
  );

  const currentViewId = useAppSelector(
    savingsOpportunitiesCurrentViewIdSelector,
  );

  const filterTree = useAppSelector(
    savingsOpportunitiesCurrentViewFiltersSelector,
  );
  const isProtected = useAppSelector(
    savingsOpportunityCurrentViewProtectedSelector,
  );

  const applyHandler = useCallback(() => {
    closeMenu();

    if (!filterTreeLocal) {
      return;
    }

    const cleanFilters = removeFiltersWithEmptyValues(filterTreeLocal);

    if (filterTree && isDeepEqual(cleanFilters, filterTree)) {
      return;
    }

    filterTreeRefForDefaultView.current = filterTreeLocal;
    dispatch(updateSavingsOpportunityCurrentViewFiltersThunk(filterTreeLocal));
  }, [dispatch, closeMenu, filterTreeLocal, filterTree]);

  const revertFiltersState = useCallback(() => {
    if (filterTree) {
      setFilterTreeLocal(adaptFilterTree(filterTree));
    } else if (!currentViewId && filterTreeRefForDefaultView.current) {
      // default view doesn't have saved filterTree, so applying last state of the filters
      setFilterTreeLocal(filterTreeRefForDefaultView.current);
    }
  }, [currentViewId, filterTree]);

  const cancelHandler = useCallback(() => {
    revertFiltersState();
    closeMenu();
  }, [closeMenu, revertFiltersState]);

  const closeHandler = useCallback(() => {
    const validForApply =
      filterTreeLocal && !filterTreeFiltersAreInvalid(filterTreeLocal);

    if (validForApply) {
      applyHandler();
    } else {
      revertFiltersState();
    }

    closeMenu();
  }, [closeMenu, applyHandler, filterTreeLocal, revertFiltersState]);

  const revertFiltersHandler = useCallback(() => {
    setFilterTreeLocal(adaptFilterTree(filterTree));
  }, [filterTree]);

  useEffect(() => {
    setFilterTreeLocal(adaptFilterTree(filterTree));
  }, [filterTree]);

  return (
    <Fragment>
      <Button
        size="small"
        variant="outlined"
        disabled={!!(isLoading || isProtected)}
        onClick={openMenu}
        startIcon={<FilterAltOutlinedIcon />}
      >
        Filters ({filtersCount})
      </Button>

      <Popover
        open={open}
        anchorEl={anchor}
        onClose={closeHandler}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box p={2} pb={0} width={900} maxHeight={1100}>
          <FilterTree
            filterValuesQuerySource="opportunity"
            showSelectOnFirstOpening
            withIncludeAllProviderAccountsOption
            possibleFilters={possibleFilters ?? []}
            filterTree={filterTreeLocal}
            setFilters={setFilterTreeLocal}
            onRevertFilters={currentViewId ? revertFiltersHandler : undefined}
            onParentClose={closeHandler}
          />
          <FilterTreeSimpleActions
            onApply={applyHandler}
            onCancel={cancelHandler}
            filterTree={filterTreeLocal}
          />
        </Box>
      </Popover>
    </Fragment>
  );
};

const adaptFilterTree = (filterTree?: FilterTreeNode) =>
  filterTree ? addIdsToFilterTreeItems(filterTree) : undefined;
